var _a, _b, _c, _d, _e;
import { __decorate, __metadata } from "tslib";
import { Component } from 'decorators';
import { AgentContextService } from '../../../services/AgentContextService';
import { FlowVariablesService } from '../../../services/FlowVariablesService';
import { AiAgentSidebarStateService } from '../../../services/AiAgentSidebarStateService';
import { AlertService } from 'modules/shared/AlertService';
import { BlipToastService } from 'modules/shared/BlipToastService';
import { BuilderFeatures } from '../../../../BuilderFeatures';
import { extractVariableName, VARIABLE_PATTERN_GLOBAL } from '../../../utils/variableUtils';
import view from './historyVariableSidebar.view.html';
import './historyVariableSidebar.scss';
import { isMemoryTypePrompt } from '../../../types/instructions';
let HistoryVariableSidebar = class HistoryVariableSidebar {
    static $inject = ['AgentContextService', 'FlowVariablesService', 'AiAgentSidebarStateService', 'AlertService', 'BlipToastService', '$translate', '$timeout'];
    constructor(AgentContextService, FlowVariablesService, AiAgentSidebarStateService, AlertService, BlipToastService, $translate, $timeout) {
        // Properties
        this.activeTab = 'history';
        this.searchTerm = '';
        this.currentVariable = ''; // Variable currently being edited
        this.currentPromptIndex = -1; // Index of the prompt being edited
        this.currentStateId = ''; // State ID of the current agent being edited
        this.usedVariables = []; // Variables already used in other blocks
        this.isFeatureEnabled = false; // Feature toggle state
        // History variables
        this.allHistoryVariables = [];
        this.filteredHistoryVariables = [];
        // Output variables
        this.allOutputVariables = [];
        this.filteredOutputVariables = [];
        this.wasOpen = false;
        // Alert properties
        this.pendingVariable = null;
        this.pendingVariableType = 'history';
        /**
         * Handle tab change event from bds-tab-group
         */
        this.onTabChange = (event) => {
            const tabId = event.detail?.value;
            if (tabId === 'history' || tabId === 'outputs') {
                this.switchTab(tabId);
            }
        };
        /**
         * Handle search input change from BDS component
         */
        this.onSearchChange = (event) => {
            this.searchTerm = event.detail?.value || '';
            this.filterVariables();
        };
        this.AgentContextService = AgentContextService;
        this.FlowVariablesService = FlowVariablesService;
        this.AiAgentSidebarStateService = AiAgentSidebarStateService;
        this.AlertService = AlertService;
        this.BlipToastService = BlipToastService;
        this.$translate = $translate;
        this.$timeout = $timeout;
    }
    async $onInit() {
        // Check if feature is enabled
        this.isFeatureEnabled = await BuilderFeatures.isAiAgentSharedMemoryManipulationEnabled();
        this.loadVariables();
    }
    /**
     * Check if sidebar is currently open and reload variables when it opens
     */
    $doCheck() {
        const isCurrentlyOpen = this.isOpen();
        // Reload variables when sidebar opens (transitions from closed to open)
        if (isCurrentlyOpen && !this.wasOpen) {
            this.handleSidebarOpened();
        }
        this.wasOpen = isCurrentlyOpen;
    }
    /**
     * Handle sidebar opened event - extract context and reload variables
     */
    handleSidebarOpened() {
        const sidebarData = this.AiAgentSidebarStateService.getData();
        this.currentPromptIndex = sidebarData?.promptIndex ?? -1;
        this.currentVariable = sidebarData?.currentVariable || '';
        this.currentStateId = sidebarData?.currentStateId || '';
        this.extractVariableFromPrompt();
        this.usedVariables = this.extractUsedVariablesFromInstructions();
        this.loadVariables();
        this.searchTerm = '';
    }
    /**
     * Extract variable from the current prompt content
     * Handles cases where sidebarData was cleared but user reopens sidebar
     */
    extractVariableFromPrompt() {
        if (!this.hasValidPrompt()) {
            return;
        }
        const prompt = this.agentConfiguration.prompt[this.currentPromptIndex];
        const extractedVar = this.extractVariableFromContent(prompt.content);
        if (extractedVar && this.shouldUpdateCurrentVariable(extractedVar)) {
            this.currentVariable = extractedVar;
        }
    }
    /**
     * Check if we have a valid prompt to extract from
     */
    hasValidPrompt() {
        return this.currentPromptIndex >= 0 &&
            !!this.agentConfiguration?.prompt?.[this.currentPromptIndex]?.content;
    }
    /**
     * Extract variable name from prompt content ({{variableName}})
     */
    extractVariableFromContent(content) {
        const result = extractVariableName(content);
        return result || null;
    }
    /**
     * Check if current variable should be updated with extracted variable
     */
    shouldUpdateCurrentVariable(extractedVar) {
        return !this.currentVariable || this.currentVariable !== extractedVar;
    }
    /**
     * Extract variables used in the agent's instructions (prompts)
     * Excludes the current prompt being edited (based on promptIndex)
     */
    extractUsedVariablesFromInstructions() {
        const usedVars = [];
        if (!this.agentConfiguration?.prompt) {
            return usedVars;
        }
        // Iterate through all prompts/instructions
        this.agentConfiguration.prompt.forEach((prompt, index) => {
            // Skip the current prompt being edited
            if (index === this.currentPromptIndex) {
                return;
            }
            // Check if it's a history variable prompt (role === 'variable' or 'short-term-memory')
            if (isMemoryTypePrompt(prompt)) {
                // Extract variables from content (anything inside {{ }})
                if ('content' in prompt) {
                    const content = prompt.content;
                    let match;
                    while ((match = VARIABLE_PATTERN_GLOBAL.exec(content)) !== null) {
                        // Extract captured group and trim
                        const varName = match[1]?.trim();
                        if (varName) {
                            usedVars.push(varName);
                        }
                    }
                    VARIABLE_PATTERN_GLOBAL.lastIndex = 0;
                }
            }
        });
        // Remove duplicates
        return [...new Set(usedVars)];
    }
    /**
     * Load all available variables from both services and mark used ones
     */
    loadVariables() {
        const unavailableTooltip = this.$translate.instant('historyVariable.variableInUse');
        // Load history variables
        const historyVars = this.AgentContextService.getAllContexts();
        // Filter out:
        // 1. The variable belonging to the current agent being edited
        // 2. Agents without memory enabled (hasMemoryEnabled === false or undefined)
        const filteredHistoryVars = historyVars.filter(variable => variable.stateId !== this.currentStateId &&
            variable.hasMemoryEnabled === true);
        this.allHistoryVariables = filteredHistoryVars.map(variable => {
            const variableName = variable.agentContext;
            // Check if this variable is used in OTHER blocks (not the current one)
            const isUsedInOtherBlock = this.usedVariables.includes(variableName);
            // The variable is "currently being edited" if it matches the currentVariable
            // and we have a valid promptIndex (meaning we're editing an existing block)
            const isCurrentlyEditing = variableName === this.currentVariable &&
                this.currentPromptIndex >= 0;
            return {
                ...variable,
                $disabled: isUsedInOtherBlock,
                $tooltip: isUsedInOtherBlock ? unavailableTooltip : undefined,
                $isUsed: isUsedInOtherBlock,
                $isCurrentlyEditing: isCurrentlyEditing,
            };
        });
        // Sort: currently editing first, then available, then used in other blocks
        this.allHistoryVariables.sort((a, b) => {
            if (a.$isCurrentlyEditing)
                return -1;
            if (b.$isCurrentlyEditing)
                return 1;
            if (a.$isUsed === b.$isUsed)
                return 0;
            return a.$isUsed ? 1 : -1;
        });
        this.filteredHistoryVariables = [...this.allHistoryVariables];
        // Load output variables (now flow variables) from FlowVariablesService
        const flowVariables = this.FlowVariablesService.getAllVariables();
        // Convert to OutputVariableWithStatus format
        this.allOutputVariables = flowVariables.map(variable => {
            const variableName = variable.variableName;
            // Check if this variable is used in OTHER blocks
            const isUsedInOtherBlock = this.usedVariables.includes(variableName);
            // The variable is "currently being edited" if it matches and we're editing a block
            const isCurrentlyEditing = variableName === this.currentVariable &&
                this.currentPromptIndex >= 0;
            return {
                ...variable,
                $disabled: isUsedInOtherBlock,
                $tooltip: isUsedInOtherBlock ? unavailableTooltip : undefined,
                $isUsed: isUsedInOtherBlock,
                $isCurrentlyEditing: isCurrentlyEditing,
            };
        });
        // Sort: currently editing first, then available, then used in other blocks
        this.allOutputVariables.sort((a, b) => {
            if (a.$isCurrentlyEditing)
                return -1;
            if (b.$isCurrentlyEditing)
                return 1;
            if (a.$isUsed === b.$isUsed)
                return 0;
            return a.$isUsed ? 1 : -1;
        });
        this.filteredOutputVariables = [...this.allOutputVariables];
    }
    /**
     * Switch between tabs
     */
    switchTab(tab) {
        this.activeTab = tab;
        this.searchTerm = '';
        this.filterVariables();
    }
    /**
     * Filter variables based on search term and active tab
     */
    filterVariables() {
        if (!this.searchTerm || this.searchTerm.trim() === '') {
            this.filteredHistoryVariables = [...this.allHistoryVariables];
            this.filteredOutputVariables = [...this.allOutputVariables];
            return;
        }
        const term = this.searchTerm.toLowerCase();
        // Filter history variables
        this.filteredHistoryVariables = this.allHistoryVariables.filter(variable => {
            const stateName = (variable.stateName || '').toLowerCase();
            const agentContext = variable.agentContext.toLowerCase();
            return stateName.includes(term) || agentContext.includes(term);
        });
        // Filter output variables
        this.filteredOutputVariables = this.allOutputVariables.filter(variable => {
            const stateName = (variable.stateName || '').toLowerCase();
            const variableName = variable.variableName.toLowerCase();
            const source = (variable.source || '').toLowerCase();
            const actionType = (variable.actionType || '').toLowerCase();
            return stateName.includes(term) ||
                variableName.includes(term) ||
                source.includes(term) ||
                actionType.includes(term);
        });
    }
    /**
     * Helper: Get variable title based on type
     */
    getVariableTitle(variable, type) {
        if (type === 'history') {
            return variable.agentContext;
        }
        return variable.variableName;
    }
    /**
     * Helper: Get variable subtitle based on type
     */
    getVariableSubtitle(variable, type) {
        if (type === 'history') {
            return variable.stateName || variable.stateId;
        }
        // For output variables (now flow variables), show source and action type if available
        if (variable.source === 'configuration') {
            return 'Configuration';
        }
        const sourceLabel = variable.actionType || variable.source || 'Unknown';
        return `${variable.stateName} - ${sourceLabel}`;
    }
    /**
     * Show confirmation alert
     */
    async showConfirmationAlert(variable, type) {
        this.pendingVariable = variable;
        this.pendingVariableType = type;
        await this.AlertService.show({
            title: this.$translate.instant('historyVariable.alerts.changeVariable.title'),
            body: this.$translate.instant('historyVariable.alerts.changeVariable.body'),
            variant: 'warning',
            icon: 'warning',
            buttons: {
                cancel: this.$translate.instant('historyVariable.alerts.changeVariable.cancel'),
                confirm: this.$translate.instant('historyVariable.alerts.changeVariable.confirm'),
            },
            callback: {
                cancelButtonAction: () => { this.cancelChange(); },
                confirmButtonAction: async () => { this.confirmChange(); },
            },
        });
    }
    /**
     * Handle alert confirmation
     */
    confirmChange() {
        if (this.pendingVariable && this.pendingVariableType) {
            // Proceed with the variable selection
            if (this.pendingVariableType === 'history') {
                const variable = this.pendingVariable;
                this.proceedWithHistorySelection(variable);
            }
            else {
                const variable = this.pendingVariable;
                this.proceedWithOutputSelection(variable);
            }
        }
        this.pendingVariable = null;
    }
    /**
     * Handle alert cancellation
     */
    cancelChange() {
        this.pendingVariable = null;
    }
    /**
     * Unified variable selection handler
     */
    selectVariable(variable, type) {
        if (type === 'history') {
            this.selectHistoryVariable(variable);
        }
        else {
            this.selectOutputVariable(variable);
        }
    }
    /**
     * Select a history variable and close sidebar
     */
    async selectHistoryVariable(variable) {
        // If variable is disabled or is currently being edited, do nothing
        if (variable.$disabled || variable.$isCurrentlyEditing) {
            return;
        }
        // If there's a current variable being changed, show confirmation
        if (this.currentVariable && this.currentVariable !== variable.agentContext) {
            this.showConfirmationAlert(variable, 'history');
            return;
        }
        this.proceedWithHistorySelection(variable);
    }
    /**
     * Proceed with history variable selection (after confirmation if needed)
     */
    async proceedWithHistorySelection(variable) {
        // Get current sidebarData to preserve targetPromptId and promptIndex
        const sidebarData = this.AiAgentSidebarStateService.getData();
        const targetPromptId = sidebarData?.targetPromptId;
        const promptIndex = sidebarData?.promptIndex;
        // Extract historyLength from the selected variable for config sidebar
        const historyLength = variable.historyLength || 50;
        // Update sidebarData with selectedVariable, targetPromptId, and historyLength
        this.AiAgentSidebarStateService.open('historyVariable', {
            selectedVariable: variable.agentContext, // Just the variable name without {{}}
            targetPromptId: targetPromptId, // Preserve the ID of the prompt being edited
            promptIndex: promptIndex, // Preserve the promptIndex
            selectedVariableHistoryLength: historyLength, // Pass the historyLength for config sidebar
        });
        // Close sidebar immediately
        this.AiAgentSidebarStateService.close();
        // Show success toast with title and subtitle
        const successTitle = await this.$translate('historyVariable.toast.historyAdded');
        const successSubtitle = await this.$translate('historyVariable.toast.historyAddedSubtitle');
        this.BlipToastService.show('success', {
            title: successTitle,
            msg: successSubtitle,
        });
        // Call the onSelect callback with the selected variable AND promptIndex (for legacy support)
        if (this.onSelect) {
            this.onSelect({
                variable: variable.agentContext,
                promptIndex: this.currentPromptIndex,
            });
        }
    }
    /**
     * Select an output variable and close sidebar
     */
    async selectOutputVariable(variable) {
        // If variable is disabled or is currently being edited, do nothing
        if (variable.$disabled || variable.$isCurrentlyEditing) {
            return;
        }
        // If there's a current variable being changed, show confirmation
        if (this.currentVariable && this.currentVariable !== variable.variableName) {
            this.showConfirmationAlert(variable, 'output');
            return;
        }
        this.proceedWithOutputSelection(variable);
    }
    /**
     * Proceed with output variable selection (after confirmation if needed)
     */
    async proceedWithOutputSelection(variable) {
        // Get current sidebarData to preserve targetPromptId and promptIndex
        const sidebarData = this.AiAgentSidebarStateService.getData();
        const targetPromptId = sidebarData?.targetPromptId;
        const promptIndex = sidebarData?.promptIndex;
        // Update sidebarData with selectedVariable and targetPromptId
        this.AiAgentSidebarStateService.open('historyVariable', {
            selectedVariable: variable.variableName, // Just the variable name without {{}}
            targetPromptId: targetPromptId, // Preserve the ID of the prompt being edited
            promptIndex: promptIndex, // Preserve the promptIndex
        });
        // Close sidebar immediately
        this.AiAgentSidebarStateService.close();
        // Show success toast with title and subtitle
        const successTitle = await this.$translate('historyVariable.toast.outputAdded');
        const successSubtitle = await this.$translate('historyVariable.toast.outputAddedSubtitle');
        this.BlipToastService.show('success', {
            title: successTitle,
            msg: successSubtitle,
        });
        // Call the onSelect callback with the selected variable AND promptIndex (for legacy support)
        if (this.onSelect) {
            this.onSelect({
                variable: variable.variableName,
                promptIndex: this.currentPromptIndex,
            });
        }
    }
    /**
     * Close the sidebar
     */
    closeSidebar() {
        this.AiAgentSidebarStateService.close();
    }
    /**
     * Check if sidebar is currently open
     */
    isOpen() {
        // If feature is disabled, do not open the sidebar
        if (!this.isFeatureEnabled) {
            return false;
        }
        return this.AiAgentSidebarStateService.isOpen('historyVariable');
    }
};
HistoryVariableSidebar.$inject = [
    'AgentContextService',
    'FlowVariablesService',
    'AiAgentSidebarStateService',
    'AlertService',
    'BlipToastService',
    '$translate',
    '$timeout',
];
HistoryVariableSidebar = __decorate([
    Component({
        controllerAs: '$ctrl',
        template: view,
        bindings: {
            onSelect: '&',
            agentConfiguration: '<', // Receive agent configuration to check used variables
        },
    }),
    __metadata("design:paramtypes", [typeof (_a = typeof AgentContextService !== "undefined" && AgentContextService) === "function" ? _a : Object, typeof (_b = typeof FlowVariablesService !== "undefined" && FlowVariablesService) === "function" ? _b : Object, typeof (_c = typeof AiAgentSidebarStateService !== "undefined" && AiAgentSidebarStateService) === "function" ? _c : Object, typeof (_d = typeof AlertService !== "undefined" && AlertService) === "function" ? _d : Object, typeof (_e = typeof BlipToastService !== "undefined" && BlipToastService) === "function" ? _e : Object, Object, Object])
], HistoryVariableSidebar);
export { HistoryVariableSidebar };
